/**
 * The Codex screen's logic, kept apart from the UI: code fields, what has
 * changed since loading, validation and reading codes pasted from a
 * spreadsheet.
 */

export const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2));

/** Every code field, in the order the grid offers them. main ones show by default. */
export const CODE_FIELDS = [
  { field: "longCode", label: "Long code", main: true, required: true },
  { field: "description1", label: "Description", main: true, required: true },
  { field: "inUse", label: "In use", main: true },
  { field: "filter", label: "Filter", main: true },
  { field: "unitCode", label: "Unit", main: true },
  { field: "typeCode", label: "Type code", main: true },
  { field: "camelCode", label: "Camel code", main: true },
  { field: "shortCode", label: "Short code" },
  { field: "description2", label: "Description 2" },
  { field: "default", label: "Default" },
  { field: "codeDate", label: "Code date" },
  { field: "direction", label: "Direction" },
  { field: "attributeID", label: "Attribute ID" },
  { field: "attributeGroup", label: "Attribute group" },
  { field: "replacementBehaviour", label: "Replacement behaviour" },
  { field: "uiOrderPrimary", label: "UI order (primary)" },
  { field: "uiOrderSecondary", label: "UI order (secondary)" },
  { field: "calculationGroup", label: "Calculation group" },
  { field: "calculationGroupRole", label: "Calculation group role" },
  { field: "codeList", label: "Code list" },
  { field: "codeListAgency", label: "Code list agency" },
  { field: "codeListFilter", label: "Code list filter" },
  { field: "codeListSource", label: "Code list source" },
  { field: "tensorID", label: "Tensor ID" },
];
const EDITABLE = CODE_FIELDS.map((f) => f.field);

/** The code list fields shown above the grid. */
export const HEADER_FIELDS = ["name", "agency", "dataStandard", "dataStandardVersion", "filter", "notes", "locked"];

export const newCodeList = () => ({
  name: "",
  dataStandard: "",
  dataStandardVersion: "",
  agency: "",
  tableID: "",
  filter: "",
  notes: "",
  locked: "",
  codes: [],
});

export const newCode = (tableID) => ({
  id: uuid(),
  tableID: tableID || "",
  ...Object.fromEntries(EDITABLE.map((f) => [f, ""])),
  inUse: "Yes",
});

/** Codes with a local id each, for the grid. */
export const withLocalIds = (codes = []) => codes.map((c) => ({ ...c, id: c.id || c.uuid || c._id || uuid() }));

/** Whether a code list's locked value means locked (e.g. "Y", "Yes", "true"). */
export const isLocked = (value) => /^(y|yes|true|1|locked)$/i.test(String(value ?? "").trim());

const same = (a, b) => String(a ?? "") === String(b ?? "");

/**
 * What has changed since the list was loaded: { added, changed, deleted,
 * header, rowState: { [id]: "new" | "changed" }, count, isDirty }.
 */
export const changesSince = (original, current) => {
  if (!current) return { added: 0, changed: 0, deleted: 0, header: false, rowState: {}, count: 0, isDirty: false };
  const before = Object.fromEntries((original?.codes || []).map((c) => [c.id, c]));
  const rowState = {};
  let added = 0;
  let changed = 0;
  (current.codes || []).forEach((c) => {
    const was = before[c.id];
    if (!was) {
      rowState[c.id] = "new";
      added += 1;
    } else if (EDITABLE.some((f) => !same(was[f], c[f]))) {
      rowState[c.id] = "changed";
      changed += 1;
    }
  });
  const now = new Set((current.codes || []).map((c) => c.id));
  const deleted = (original?.codes || []).filter((c) => !now.has(c.id)).length;
  const header = !original || HEADER_FIELDS.some((f) => !same(original[f], current[f])) || !same(original.tableID, current.tableID);
  const count = added + changed + deleted;
  return { added, changed, deleted, header, rowState, count, isDirty: count > 0 || header };
};

/**
 * Problems that stop a save: { byRow: { [id]: { longCode?, description1? } },
 * messages: [string] }. Long code and description are required, and long
 * codes must be unique (ignoring case and spaces at the ends).
 */
export const validate = (codeList) => {
  const byRow = {};
  const flag = (id, field, text) => ((byRow[id] = byRow[id] || {})[field] = text);
  const codes = codeList?.codes || [];
  const counts = {};
  codes.forEach((c) => {
    const key = String(c.longCode ?? "").trim().toLowerCase();
    if (key) counts[key] = (counts[key] || 0) + 1;
  });
  let missingCode = 0;
  let missingDescription = 0;
  const duplicates = new Set();
  codes.forEach((c) => {
    const key = String(c.longCode ?? "").trim().toLowerCase();
    if (!key) {
      missingCode += 1;
      flag(c.id, "longCode", "A long code is required");
    } else if (counts[key] > 1) {
      duplicates.add(String(c.longCode).trim());
      flag(c.id, "longCode", "This long code is used more than once");
    }
    if (!String(c.description1 ?? "").trim()) {
      missingDescription += 1;
      flag(c.id, "description1", "A description is required");
    }
  });
  const messages = [];
  if (!String(codeList?.tableID ?? "").trim()) messages.push("The code list needs an ID (e.g. A82)");
  if (!String(codeList?.name ?? "").trim()) messages.push("The code list needs a name");
  if (missingCode) messages.push(`${missingCode} code${missingCode === 1 ? " has" : "s have"} no long code`);
  if (missingDescription) messages.push(`${missingDescription} code${missingDescription === 1 ? " has" : "s have"} no description`);
  if (duplicates.size) messages.push(`Long codes used more than once: ${[...duplicates].slice(0, 5).join(", ")}${duplicates.size > 5 ? "…" : ""}`);
  return { byRow, messages };
};

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The body to save: codes without the grid's local id, each with the list's ID. */
export const toPayload = (codeList) => {
  const codes = (codeList.codes || []).map(({ id, ...rest }) => {
    const code = { ...rest, tableID: codeList.tableID };
    if (!(typeof rest.uuid === "string" && GUID.test(rest.uuid))) delete code.uuid;
    if (!(typeof rest._id === "string" && rest._id)) delete code._id;
    return code;
  });
  const payload = {
    name: codeList.name,
    dataStandard: codeList.dataStandard || "",
    dataStandardVersion: codeList.dataStandardVersion || "",
    agency: codeList.agency || "",
    tableID: codeList.tableID,
    filter: codeList.filter || "",
    notes: codeList.notes || "",
    locked: codeList.locked || "",
    codes,
  };
  if (codeList._id) payload._id = codeList._id;
  return payload;
};

/** Splits pasted text into rows of cells: tab-separated (from Excel) or CSV. */
export const parseTable = (text) => {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
  if (!lines.length) return [];
  if (lines[0].includes("\t")) return lines.map((l) => l.split("\t").map((c) => c.trim()));
  // CSV with quoted cells
  return lines.map((line) => {
    const cells = [];
    let cell = "";
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') { cell += '"'; i += 1; }
        else if (ch === '"') quoted = false;
        else cell += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === ",") { cells.push(cell.trim()); cell = ""; }
      else cell += ch;
    }
    cells.push(cell.trim());
    return cells;
  });
};

const normalise = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const FIELD_BY_NAME = Object.fromEntries(
  CODE_FIELDS.flatMap((f) => [[normalise(f.field), f.field], [normalise(f.label), f.field]]).concat([
    [normalise("code"), "longCode"],
    [normalise("description"), "description1"],
  ])
);

/**
 * Reads pasted codes. If the first row names fields ("longCode",
 * "Description", …) those are the columns; otherwise the columns are long
 * code, then description, then the other main fields in order.
 * Returns { columns: [field], rows: [{ field: value }] }.
 */
export const readPastedCodes = (text) => {
  const table = parseTable(text);
  if (!table.length) return { columns: [], rows: [] };
  const headerFields = table[0].map((c) => FIELD_BY_NAME[normalise(c)]);
  const hasHeader = headerFields.filter(Boolean).length >= 1 && headerFields.includes("longCode");
  const columns = hasHeader ? headerFields : CODE_FIELDS.filter((f) => f.main).map((f) => f.field).slice(0, table[0].length);
  const rows = (hasHeader ? table.slice(1) : table).map((cells) =>
    Object.fromEntries(columns.map((field, i) => [field, cells[i] ?? ""]).filter(([field]) => field))
  );
  return { columns: columns.filter(Boolean), rows };
};

/**
 * What pasting would do to the codes, matched by long code: [{ row, action:
 * "new" | "update" | "same" | "skip", existing }].
 */
export const previewPaste = (codes, pasted) => {
  const byCode = Object.fromEntries((codes || []).map((c) => [String(c.longCode ?? "").trim().toLowerCase(), c]));
  return pasted.rows.map((row) => {
    const key = String(row.longCode ?? "").trim().toLowerCase();
    if (!key) return { row, action: "skip" };
    const existing = byCode[key];
    if (!existing) return { row, action: "new" };
    const differs = Object.keys(row).some((f) => f !== "longCode" && !same(existing[f], row[f]));
    return { row, action: differs ? "update" : "same", existing };
  });
};

/** The codes after applying a paste preview (new codes go at the end). */
export const applyPaste = (codes, preview, tableID) => {
  const updates = Object.fromEntries(preview.filter((p) => p.action === "update").map((p) => [p.existing.id, p.row]));
  const next = (codes || []).map((c) => (updates[c.id] ? { ...c, ...updates[c.id], longCode: c.longCode } : c));
  preview.filter((p) => p.action === "new").forEach((p) => next.push({ ...newCode(tableID), ...p.row }));
  return next;
};

/** Code lists grouped by data standard, each group sorted by ID: [{ standard, lists }]. */
export const groupLists = (lists, search) => {
  const text = String(search || "").trim().toLowerCase();
  const shown = (lists || []).filter(
    (l) => !text || [l.tableID, l.name, l.dataStandard].some((v) => String(v || "").toLowerCase().includes(text))
  );
  const groups = {};
  shown.forEach((l) => (groups[l.dataStandard || "No data standard"] = groups[l.dataStandard || "No data standard"] || []).push(l));
  return Object.entries(groups)
    .sort(([a], [b]) => (a === "No data standard") - (b === "No data standard") || a.localeCompare(b))
    .map(([standard, items]) => ({ standard, lists: items.sort((a, b) => String(a.tableID).localeCompare(String(b.tableID))) }));
};
